import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { APIServiceType } from '@/api/types';
import handleUserContextData from './helpers/handle-user-context-data';
import { fetchUserContextApi } from './fetch-user-context';
import getCurrentSessionDetails from '../account/session/get-current-session';
import { useStore } from '@/store/useStore';

const USER_CONTEXT_QUERY_KEY = ['user-context'] as const;

export const useUserContext = () => {
  const hasActiveSession = useStore(
    (s) => s.userSessionContext.hasActiveSession,
  );
  const gameResetRevision = useStore((s) => s.gameResetRevision);
  const sessionResetRevision = useStore((s) => s.sessionResetRevision);

  const query = useQuery({
    queryKey: USER_CONTEXT_QUERY_KEY,
    queryFn: fetchUserContextApi,
    meta: { apiService: APIServiceType.USER_CONTEXT },
    enabled: hasActiveSession,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const { data, refetch } = query;

  // User-context is cheap-to-update state (flags, eligibilities) and the game client does
  // not need to be torn down when it changes. We keep `refetchOnMount: 'always'` so the
  // store stays fresh, but drop the cache-diff soft-reload — it was a noisy source of
  // 2nd/3rd reloads on launch for data that flips frequently vs persisted hydrate.
  useEffect(() => {
    if (!hasActiveSession) return;
    refetch();
  }, [hasActiveSession, gameResetRevision, sessionResetRevision, refetch]);

  useEffect(() => {
    if (!data) return;
    handleUserContextData(data);
  }, [data]);

  useEffect(() => {
    if (!hasActiveSession) return;
    getCurrentSessionDetails();
  }, [hasActiveSession]);

  const fetchUserContext = useCallback(() => refetch(), [refetch]);

  return {
    fetchUserContext,
  };
};

export default useUserContext;
